/**
 * InboxBulkBar - Toolbar shown when emails are checked in the inbox.
 * Offers bulk actions: mark read, mark unread, trash, resolve, clear selection.
 */
import styles from './InboxBulkBar.module.css';

interface InboxBulkBarProps {
  count: number;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onTrash: () => void;
  trashing?: boolean;
  /** Show "Mark Resolved" button (only on Important tab). */
  onMarkResolved?: () => void;
  resolving?: boolean;
  onClear: () => void;
}

const InboxBulkBar: React.FC<InboxBulkBarProps> = ({
  count,
  onMarkRead,
  onMarkUnread,
  onTrash,
  trashing = false,
  onMarkResolved,
  resolving = false,
  onClear,
}) => (
  <div className={styles.bar} data-section="bulk_actions">
    <span className={styles.count}>{count} selected</span>
    <div className={styles.actions}>
      <button
        className={styles.actionBtn}
        onClick={onMarkRead}
        type="button"
        data-action="bulk_mark_read"
        title="Mark selected as read"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Mark Read
      </button>
      <button
        className={styles.actionBtn}
        onClick={onMarkUnread}
        type="button"
        data-action="bulk_mark_unread"
        title="Mark selected as unread"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="4" />
        </svg>
        Mark Unread
      </button>
      {onMarkResolved && (
        <button
          className={`${styles.actionBtn} ${styles.resolveBtn}`}
          onClick={onMarkResolved}
          disabled={resolving}
          type="button"
          data-action="bulk_resolve"
          title="Mark selected action items as resolved"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {resolving ? 'Resolving...' : 'Mark Resolved'}
        </button>
      )}
      <button
        className={`${styles.actionBtn} ${styles.trashBtn}`}
        onClick={onTrash}
        disabled={trashing}
        type="button"
        data-action="bulk_trash"
        title="Move selected to trash — hides from unmapped view"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
        {trashing ? 'Trashing...' : 'Trash'}
      </button>
      <button
        className={`${styles.actionBtn} ${styles.clearBtn}`}
        onClick={onClear}
        type="button"
        data-action="bulk_clear_selection"
      >
        Clear
      </button>
    </div>
  </div>
);

export default InboxBulkBar;
